import { type AccessPackageCode } from '@choco-admin/types';
import { useCallback, useState } from 'react';

import { useSuppliersPageUpdateSupplierAccessPackageMutation } from '../../../generated';

export interface UseUpdateSupplierAccessPackageResponse {
  /** Moves one distributor to `accessPackage`. Throws with a reason on failure. */
  updateAccessPackage: (
    supplierId: string,
    accessPackage: AccessPackageCode
  ) => Promise<void>;
  isSaving: boolean;
}

/**
 * Writes a distributor's access package.
 *
 * Three ways this fails, and they need checking separately.
 *
 * `apollo-client.ts` sets `mutate.errorPolicy: 'all'`, so a rejected mutation
 * resolves rather than throwing and the rejection arrives in `result.errors`. A
 * hook that only wraps the call in try/catch reports a save that never happened.
 *
 * The response is a union, so `SupplierUpdateAccessPackageError` carries a
 * `message` and no write occurred.
 *
 * And `SupplierUpdateAccessPackageSuccess` still carries `errors: [SupplierUpdateError!]!`,
 * one per distributor, because the mutation takes a list. A success with a
 * non-empty `errors` array means that distributor was not moved, which is the
 * trap the legacy admin documented and checked for
 * (`useAccessPackageEdit.tsx`, `checkIfUpdateError`).
 *
 * The refetch is not optional either. Legacy's comment says it plainly: the
 * mutation "does not trigger a cache update of the existing Supplier entity in
 * Apollo", so without it the screen keeps showing the old tier.
 */
export const useUpdateSupplierAccessPackage =
  (): UseUpdateSupplierAccessPackageResponse => {
    const [mutate] = useSuppliersPageUpdateSupplierAccessPackageMutation({
      refetchQueries: ['GetSupplierDetailsData'],
      awaitRefetchQueries: true,
    });
    const [isSaving, setIsSaving] = useState(false);

    const updateAccessPackage = useCallback(
      async (
        supplierId: string,
        accessPackage: AccessPackageCode
      ): Promise<void> => {
        setIsSaving(true);

        try {
          const result = await mutate({
            variables: { suppliers: [{ supplierId, accessPackage }] },
          });

          const [transportError] = result.errors ?? [];
          if (transportError) throw new Error(transportError.message);

          const response = result.data?.updateSupplierAccessPackage;
          if (!response) {
            throw new Error('The access package change returned no result.');
          }

          // Narrowed on `errors`, not on `__typename` or `message`: codegen types
          // both of those as optional, and an optional property narrows neither
          // arm of the union. `errors` is the one required field the success arm
          // has and the error arm does not.
          if (!('errors' in response)) {
            throw new Error(
              response.message ?? 'The access package could not be changed.'
            );
          }

          // A success that reports an error for this distributor did not move it.
          const [perSupplierError] = response.errors ?? [];
          if (perSupplierError) throw new Error(perSupplierError.message);
        } finally {
          setIsSaving(false);
        }
      },
      [mutate]
    );

    return { updateAccessPackage, isSaving };
  };
